import React, { useState, useCallback } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Collapsible, ModalDialog } from '@openedx/paragon';

import messages from './messages';
import './_lti-tools-tab.scss';
import {
  LtiConfig,
  deleteLtiConfig,
  deleteLtiConfigsForCourse,
  listLtiConfigs,
} from './data/api';
import AiWizard from './components/AiWizard';
import EditConfigPanel from './components/EditConfigPanel';
import StudioBlockPanel from './components/StudioBlockPanel';

// ─── Tool Registry ────────────────────────────────────────────────────────────

interface LtiToolDefinition {
  id: string;
  labelMessageDescriptor: { id: string; defaultMessage: string };
  descriptionMessageDescriptor: { id: string; defaultMessage: string };
}

const TOOL_REGISTRY: LtiToolDefinition[] = [
  {
    id: 'skill_evaluator',
    labelMessageDescriptor: messages.toolSkillEvaluatorLabel,
    descriptionMessageDescriptor: messages.toolSkillEvaluatorDescription,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const LTI_TYPE_LABELS: Record<string, string> = {
  checkin: 'Check-in',
  pre_assessment: 'Pre-Assessment',
  post_assessment: 'Post-Assessment',
};

const SKILL_EVALUATOR_COMPONENT_NAMES = [
  'Pre-Assessment',
  'Check-In',
  'Post-Assessment',
  'Skill Report',
];

// ─── Main Component ───────────────────────────────────────────────────────────

const LtiToolsTab: React.FC = () => {
  const intl = useIntl();

  const [courseId, setCourseId] = useState('');
  const [loadedCourseId, setLoadedCourseId] = useState<string | null>(null);

  const [configs, setConfigs] = useState<LtiConfig[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listSuccess, setListSuccess] = useState<string | null>(null);
  const [resettingConfigs, setResettingConfigs] = useState(false);
  const [pendingDeletion, setPendingDeletion] = useState<string | 'all' | null>(null);

  const [expandedConfigId, setExpandedConfigId] = useState<string | null>(null);
  const [hoveredConfigId, setHoveredConfigId] = useState<string | null>(null);
  const [selectedToolId, setSelectedToolId] = useState<string>('');
  const [showWizard, setShowWizard] = useState(false);

  const selectedTool = TOOL_REGISTRY.find((t) => t.id === selectedToolId) ?? null;
  const selectedToolDescription = selectedTool
    ? intl.formatMessage(selectedTool.descriptionMessageDescriptor).split('\n\n')
    : [];

  const handleToolChange = (toolId: string) => {
    setSelectedToolId(toolId);
    setCourseId('');
    setLoadedCourseId(null);
    setConfigs([]);
    setExpandedConfigId(null);
    setListSuccess(null);
    setListError(null);
    setShowWizard(false);
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const loadConfigs = useCallback(async (cid: string) => {
    if (!cid.trim()) { return; }
    setListLoading(true);
    setListError(null);
    setConfigs([]);
    setExpandedConfigId(null);
    setShowWizard(false);
    try {
      const data = await listLtiConfigs(cid.trim());
      setConfigs(data);
      setLoadedCourseId(cid.trim());
    } catch {
      setListError(intl.formatMessage(messages.configListErrorMessage));
    } finally {
      setListLoading(false);
    }
  }, [intl]);

  const handleWizardSaved = async () => {
    setShowWizard(false);
    setListSuccess(intl.formatMessage(messages.wizardSaveSuccessMessage));
    if (loadedCourseId) {
      await loadConfigs(loadedCourseId);
    }
  };

  const handleDelete = async (configId: string) => {
    try {
      await deleteLtiConfig(configId);
      setConfigs((prev) => prev.filter((c) => c.config_id !== configId));
      if (expandedConfigId === configId) { setExpandedConfigId(null); }
    } catch {
      setListError(intl.formatMessage(messages.deleteErrorMessage));
    }
  };

  const handleResetConfigurations = async () => {
    if (!loadedCourseId) { return; }

    setResettingConfigs(true);
    setListError(null);
    try {
      await deleteLtiConfigsForCourse(loadedCourseId);
      setConfigs([]);
      setExpandedConfigId(null);
      setShowWizard(false);
    } catch {
      setListError(intl.formatMessage(messages.resetConfigurationsErrorMessage));
    } finally {
      setResettingConfigs(false);
    }
  };

  const handleConfirmDeletion = async () => {
    if (!pendingDeletion) { return; }
    if (pendingDeletion === 'all') {
      await handleResetConfigurations();
    } else {
      await handleDelete(pendingDeletion);
    }
    setPendingDeletion(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="lti-tools-tab">

      {/* Header */}
      <h2 className="lti-tools-tab__title">{intl.formatMessage(messages.pageTitle)}</h2>
      <p className="lti-tools-tab__description">{intl.formatMessage(messages.pageDescription)}</p>

      {/* Tool type selector */}
      <div className="card lti-tools-tab__card">
        <div className="card-body">
          <label htmlFor="lti-tool-selector" className="form-label lti-tools-tab__field-label">
            {intl.formatMessage(messages.toolSelectorLabel)}
          </label>
          <select
            id="lti-tool-selector"
            className="form-select lti-tools-tab__tool-selector"
            value={selectedToolId}
            onChange={(e) => handleToolChange(e.target.value)}
          >
            <option value="">{intl.formatMessage(messages.toolSelectorPlaceholder)}</option>
            {TOOL_REGISTRY.map((tool) => (
              <option key={tool.id} value={tool.id}>
                {intl.formatMessage(tool.labelMessageDescriptor)}
              </option>
            ))}
          </select>

          {selectedTool && (
            <div className="alert alert-info lti-tools-tab__tool-description">
              <div className="lti-tools-tab__tool-description-heading">
                <span className="lti-tools-tab__tool-description-icon">ℹ️</span>
                <strong>{intl.formatMessage(selectedTool.labelMessageDescriptor)}</strong>
              </div>
              <p className="lti-tools-tab__tool-description-paragraph">{selectedToolDescription[0]}</p>
              <ol className="lti-tools-tab__tool-description-list">
                {SKILL_EVALUATOR_COMPONENT_NAMES.map((name, index) => (
                  <li key={name} className="lti-tools-tab__tool-description-item">
                    <strong>{name}:</strong>{' '}
                    {selectedToolDescription[index + 1]}
                  </li>
                ))}
              </ol>
              {selectedToolDescription.slice(5).map((paragraph) => (
                <p key={paragraph} className="lti-tools-tab__tool-description-paragraph lti-tools-tab__tool-description-paragraph--follow-up">{paragraph}</p>
              ))}
              <p className="lti-tools-tab__studio-setup-hint">
                {intl.formatMessage(messages.toolStudioSetupHint)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Course ID lookup */}
      {selectedTool && (
        <div className="card lti-tools-tab__card">
          <div className="card-body">
            <label htmlFor="lti-course-id" className="form-label lti-tools-tab__field-label">
              {intl.formatMessage(messages.courseIdLabel)}
            </label>
            <div className="lti-tools-tab__course-lookup">
              <input
                id="lti-course-id"
                className="form-control lti-tools-tab__course-id-input"
                value={courseId}
                placeholder={intl.formatMessage(messages.courseIdPlaceholder)}
                onChange={(e) => setCourseId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { loadConfigs(courseId); } }}
              />
              <button
                type="button"
                className="btn btn-primary lti-tools-tab__load-button"
                onClick={() => loadConfigs(courseId)}
                disabled={listLoading || !courseId.trim()}
              >
                {listLoading ? 'Loading…' : intl.formatMessage(messages.loadConfigsButton)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global alerts */}
      {listError && (
        <div className="alert alert-danger alert-dismissible lti-tools-tab__alert">
          {listError}
          <button type="button" className="lti-tools-tab__alert-close" onClick={() => setListError(null)} aria-label="Close">×</button>
        </div>
      )}
      {listSuccess && (
        <div className="alert alert-success alert-dismissible lti-tools-tab__alert">
          {listSuccess}
          <button type="button" className="lti-tools-tab__alert-close" onClick={() => setListSuccess(null)} aria-label="Close">×</button>
        </div>
      )}

      {/* Config list + action bar */}
      {loadedCourseId && (
        <>
          <div className="lti-tools-tab__config-list-header">
            <h4 className="lti-tools-tab__config-list-title">
              Configurations for <code>{loadedCourseId}</code>
            </h4>
            {configs.length === 0 ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setShowWizard(true)}
              >
                {intl.formatMessage(messages.wizardLaunchButton)}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => setPendingDeletion('all')}
                disabled={resettingConfigs}
              >
                {resettingConfigs
                  ? 'Deleting configurations…'
                  : intl.formatMessage(messages.resetConfigurationsButton)}
              </button>
            )}
          </div>

          {configs.length === 0 ? (
            <div className="alert alert-info">{intl.formatMessage(messages.configListEmptyMessage)}</div>
          ) : (
            <>
              <div className="lti-tools-tab__config-list">
                <div className="lti-tools-tab__config-list-columns" aria-hidden="true">
                  <div className="lti-tools-tab__config-list-skill">{intl.formatMessage(messages.columnSkill)}</div>
                  <div className="lti-tools-tab__config-list-type">{intl.formatMessage(messages.columnType)}</div>
                  <div className="lti-tools-tab__config-list-scenario">{intl.formatMessage(messages.columnScenarioId)}</div>
                  <div className="lti-tools-tab__config-list-updated">{intl.formatMessage(messages.columnLastUpdated)}</div>
                  <div className="lti-tools-tab__config-list-actions">{intl.formatMessage(messages.columnActions)}</div>
                  <div className="lti-tools-tab__config-list-caret" />
                </div>
                {configs.map((c) => {
                  const isExpanded = expandedConfigId === c.config_id;
                  const contentId = `lti-config-${c.config_id}-content`;

                  return (
                    <Collapsible.Advanced
                      key={c.config_id}
                      className="lti-tools-tab__config-item"
                      open={isExpanded}
                      onOpen={() => setExpandedConfigId(c.config_id)}
                      onClose={() => setExpandedConfigId(null)}
                      unmountOnExit={false}
                    >
                      <Collapsible.Trigger
                        tag="div"
                        className={`lti-tools-tab__config-row lti-tools-tab__config-trigger ${hoveredConfigId === c.config_id ? 'lti-tools-tab__config-row--hovered' : ''}`}
                        aria-controls={contentId}
                        data-testid={`lti-config-${c.config_id}-toggle`}
                        onMouseEnter={() => setHoveredConfigId(c.config_id)}
                        onMouseLeave={() => setHoveredConfigId(null)}
                      >
                        <span className="lti-tools-tab__config-list-skill">{c.skill}</span>
                        <span className="lti-tools-tab__config-list-type">
                          <span className="lti-badge-type">{LTI_TYPE_LABELS[c.lti_type] ?? c.lti_type}</span>
                        </span>
                        <span className="lti-tools-tab__config-list-scenario"><code>{c.scenario_id}</code></span>
                        <span className="lti-tools-tab__config-list-updated">{formatDate(c.updated_at)}</span>
                        <div className="lti-tools-tab__config-list-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={(event) => {
                              event.stopPropagation();
                              setPendingDeletion(c.config_id);
                            }}
                          >
                            {intl.formatMessage(messages.deleteButton)}
                          </button>
                        </div>
                        <span className="lti-tools-tab__config-caret" aria-hidden="true">
                          {isExpanded ? '⌃' : '⌄'}
                        </span>
                      </Collapsible.Trigger>
                      <Collapsible.Body id={contentId} className="lti-tools-tab__config-body">
                        <EditConfigPanel
                          config={c}
                          onSaved={(updatedConfigs) => {
                            setConfigs((prev) => prev.map((config) => (
                              updatedConfigs.find((updated) => updated.config_id === config.config_id) ?? config
                            )));
                            setListSuccess(intl.formatMessage(messages.editSaveSuccessMessage));
                          }}
                          onClose={() => setExpandedConfigId(null)}
                        />
                        {isExpanded && <StudioBlockPanel configId={c.config_id} content="configuration" />}
                      </Collapsible.Body>
                    </Collapsible.Advanced>
                  );
                })}
              </div>
              <StudioBlockPanel configId={configs[0].config_id} content="report" />
            </>
          )}
        </>
      )}

      {/* AI Wizard */}
      {showWizard && loadedCourseId && (
        <AiWizard
          courseId={loadedCourseId}
          onSaved={handleWizardSaved}
          onCancel={() => setShowWizard(false)}
        />
      )}

      <ModalDialog
        title={pendingDeletion === 'all' ? 'Delete all configurations' : 'Delete configuration'}
        isOpen={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        hasCloseButton
      >
        <ModalDialog.Header>
          <ModalDialog.Title>{pendingDeletion === 'all' ? 'Delete all configurations' : 'Delete configuration'}</ModalDialog.Title>
        </ModalDialog.Header>
        <ModalDialog.Body>
          <p>
            {pendingDeletion === 'all'
              ? intl.formatMessage(messages.resetConfigurationsConfirmMessage)
              : intl.formatMessage(messages.deleteConfirmMessage)}
          </p>
        </ModalDialog.Body>
        <ModalDialog.Footer>
          <ModalDialog.CloseButton variant="tertiary">Cancel</ModalDialog.CloseButton>
          <button type="button" className="btn btn-danger" onClick={handleConfirmDeletion} disabled={resettingConfigs}>
            {pendingDeletion === 'all' && resettingConfigs ? 'Deleting configurations…' : 'Delete'}
          </button>
        </ModalDialog.Footer>
      </ModalDialog>
    </div>
  );
};

export default LtiToolsTab;
